import React, { forwardRef, useImperativeHandle, useState, useRef } from "react";
import { Button } from "@/modern/components/ui/button";
import { Label } from "@/modern/components/ui/label";
import { Input } from "@/modern/components/ui/input";
import { importDataApi, importMarketingTagsApi } from "@/network/network";
import { serverErrorMsg, showToast } from "@/other/utils/utils";
import { IMPORT_TYPES } from "@/other/constants/OtherKeys";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { shouldShowAsyncDialog } from "@/bryntum-lib/editors/AsyncTaskMessageEditor/SwivuAsyncMessagePopup";
import { MessageDialog } from "@bryntum/schedulerpro";
import { Download } from "lucide-react";
import { downloadFileFromData } from "@/other/utils/utils";

const MARKETING_TAGS_REQUIRED_HEADERS = ["Problem Tags", "Description", "Solution Tags"];

const validateMarketingTagsCsvHeaders = async (file: File): Promise<string | null> => {
  const text = await file.text();
  const firstLine = text.split(/\r?\n/).find((line) => line.trim());
  if (!firstLine) return "CSV file is empty.";

  const headers = firstLine.split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
  const missing = MARKETING_TAGS_REQUIRED_HEADERS.filter(
    (req) => !headers.some((h) => h.toLowerCase() === req.toLowerCase())
  );
  const extra = headers.filter(
    (h) => !MARKETING_TAGS_REQUIRED_HEADERS.some((req) => req.toLowerCase() === h.toLowerCase())
  );

  if (missing.length > 0 || extra.length > 0) {
    const parts: string[] = [];
    if (missing.length > 0) parts.push(`Missing: ${missing.join(", ")}`);
    if (extra.length > 0) parts.push(`Unexpected: ${extra.join(", ")}`);
    return `Invalid CSV headers. ${parts.join(". ")}. Expected exactly: ${MARKETING_TAGS_REQUIRED_HEADERS.join(", ")}.`;
  }

  return null;
};

const DUMMY_MARKETING_TAGS_DATA = [
  {
    "Problem Tags": "Window Perimeter Leaks",
    "Description": "Water entry at window-to-wall joints, failed perimeter sealants, glazing transitions, sill conditions, and related leakage points around window assemblies.",
    "Solution Tags": "Facade Survey, Probe Openings, Water Testing, Sealant Removal, Re-Caulking, Reglazing, Temporary Protection, Sealant Replacement, Joint Preparation"
  },
  {
    "Problem Tags": "Parapet and Coping Leaks",
    "Description": "Leakage at parapet walls, coping stones, caps, top-of-wall joints, reglets, and transition points where water enters from above or at horizontal-to-vertical changes.",
    "Solution Tags": "Facade Survey, Probe Openings, Water Testing, Sealant Removal, Re-Caulking, Sealant Replacement, Temporary Protection, Masonry Repointing, Flashing Repair, Coping Joint Repair, Scaffold Access, Rope Access"
  },
  {
    "Problem Tags": "Facade Water Infiltration",
    "Description": "General water entry through exterior wall assemblies, including cracks, failed joints, porous materials, transitions, and concealed pathways.",
    "Solution Tags": "Facade Survey, Probe Openings, Water Testing, Sealant Removal, Re-Caulking, Sealant Replacement, Masonry Repointing, Crack Repair, Flashing Repair, Temporary Protection, Scaffold Access, Rope Access"
  },
  {
    "Problem Tags": "Balcony Waterproofing Failures",
    "Description": "Water intrusion, failed waterproofing, cracked toppings, edge deterioration, railing penetrations, and drainage-related failures at balcony assemblies.",
    "Solution Tags": "Facade Survey, Probe Openings, Water Testing, Membrane Repair, Waterproofing Replacement, Railing Penetration Repair, Sealant Removal, Re-Caulking, Drainage Correction, Surface Preparation, Temporary Protection, Scaffold Access"
  },
  {
    "Problem Tags": "Concrete Spalling and Corrosion",
    "Description": "Deteriorated concrete, exposed reinforcing steel, corrosion-driven expansion, delamination, cracking, and loss of section in structural or facade concrete.",
    "Solution Tags": "Facade Survey, Probe Openings, Concrete Removal, Concrete Repair, Patch Repair, Reinforcing Steel Treatment, Corrosion Mitigation, Crack Repair, Surface Preparation, Temporary Protection, Scaffold Access, Rope Access"
  },
  {
    "Problem Tags": "Sealant and Joint System Failures",
    "Description": "Failed sealant joints, improper joint preparation, cohesive or adhesive failure, open joints, and aging joint systems across facade and envelope elements.",
    "Solution Tags": "Facade Survey, Probe Openings, Sealant Removal, Joint Preparation, Backer Rod Replacement, Re-Caulking, Sealant Replacement, Water Testing, Temporary Protection, Lift Access, Scaffold Access"
  },
  {
    "Problem Tags": "Falling Facade Hazards",
    "Description": "Loose, unstable, displaced, cracked, or deteriorated exterior materials that present a life-safety risk from falling debris or sudden release.",
    "Solution Tags": "Facade Survey, Hazard Stabilization, Emergency Protection, Debris Removal, Selective Demolition, Temporary Protection, Probe Openings, Masonry Repointing, Anchorage Repair, Concrete Repair, Terra Cotta Repair, Scaffold Access, Rope Access"
  },
  {
    "Problem Tags": "Drainage and Water Management Failures",
    "Description": "Improper drainage, blocked pathways, ponding, overflow, failed leaders, and poor water shedding at the building exterior.",
    "Solution Tags": "Facade Survey, Drainage Correction, Leader Repair, Scupper Repair, Flashing Repair, Water Testing, Temporary Protection, Sealant Replacement, Surface Preparation, Lift Access, Scaffold Access"
  },
  {
    "Problem Tags": "Roof Edge and Flashing Failures",
    "Description": "Failed flashing systems, edge metal transitions, counterflashing issues, reglet failures, and roof-to-wall transition leakage.",
    "Solution Tags": "Facade Survey, Probe Openings, Water Testing, Flashing Repair, Counterflashing Repair, Sealant Removal, Sealant Replacement, Re-Caulking, Temporary Protection, Scaffold Access, Rope Access"
  },
  {
    "Problem Tags": "Plaza Deck Failures",
    "Description": "Leakage, membrane failure, topping slab distress, drainage issues, and waterproofing breakdown at plaza decks and occupied spaces below.",
    "Solution Tags": "Facade Survey, Probe Openings, Water Testing, Membrane Repair, Waterproofing Replacement, Drainage Correction, Sealant Replacement, Surface Preparation, Temporary Protection, Selective Demolition, Scaffold Access"
  },
  {
    "Problem Tags": "Masonry Deterioration",
    "Description": "General masonry distress including open joints, weathered mortar, cracking, displacement, moisture damage, and age-related loss of integrity.",
    "Solution Tags": "Facade Survey, Probe Openings, Masonry Repointing, Brick Replacement, Stone Repair, Crack Repair, Cleaning, Temporary Protection, Water Testing, Scaffold Access, Rope Access"
  },
  {
    "Problem Tags": "Brick / Stone Spalling",
    "Description": "Surface loss, delamination, cracking, displacement, and freeze-thaw-related deterioration in brick and stone units.",
    "Solution Tags": "Facade Survey, Probe Openings, Brick Replacement, Stone Repair, Masonry Repointing, Crack Repair, Selective Demolition, Anchorage Repair, Temporary Protection, Scaffold Access, Rope Access"
  },
  {
    "Problem Tags": "Expansion Joint Failures",
    "Description": "Failed movement joints, torn or missing sealant, displaced covers, water entry at movement locations, and inability of assemblies to accommodate movement.",
    "Solution Tags": "Facade Survey, Probe Openings, Sealant Removal, Joint Preparation, Backer Rod Replacement, Sealant Replacement, Expansion Joint Repair, Water Testing, Temporary Protection, Lift Access, Scaffold Access"
  },
  {
    "Problem Tags": "Parking Garage Deterioration",
    "Description": "Concrete deterioration, water infiltration, deck distress, joint failures, corrosion, and traffic-bearing wear in parking structures.",
    "Solution Tags": "Facade Survey, Probe Openings, Concrete Removal, Concrete Repair, Patch Repair, Reinforcing Steel Treatment, Corrosion Mitigation, Expansion Joint Repair, Traffic Coating Repair, Drainage Correction, Temporary Protection, Scaffold Access"
  },
  {
    "Problem Tags": "Planter Leaks",
    "Description": "Water migration from planters into adjacent occupied or structural areas due to failed liners, waterproofing, drainage, or transitions.",
    "Solution Tags": "Facade Survey, Probe Openings, Water Testing, Waterproofing Replacement, Membrane Repair, Drainage Correction, Sealant Replacement, Temporary Protection, Selective Demolition, Surface Preparation"
  },
  {
    "Problem Tags": "Ice Dam Failures",
    "Description": "Freeze-thaw-driven leakage and damage caused by ice buildup, blocked drainage, water backup, and winter edge conditions.",
    "Solution Tags": "Facade Survey, Water Testing, Drainage Correction, Flashing Repair, Sealant Replacement, Temporary Protection, Thermal Investigation, Surface Preparation, Lift Access"
  },
  {
    "Problem Tags": "Window / Glazing Failures",
    "Description": "Failed glazing systems, perimeter leakage, cracked or failed sealants, failed gaskets, glass-related leakage, and assembly breakdown.",
    "Solution Tags": "Facade Survey, Probe Openings, Water Testing, Reglazing, Sealant Removal, Joint Preparation, Sealant Replacement, Re-Caulking, Gasket Replacement, Temporary Protection, Lift Access, Scaffold Access"
  },
  {
    "Problem Tags": "Historic Facade Deterioration",
    "Description": "Age-related distress and leakage in historic exterior assemblies requiring sensitive repair, preservation judgment, and compatible materials.",
    "Solution Tags": "Facade Survey, Probe Openings, Masonry Repointing, Stone Repair, Terra Cotta Repair, Cleaning, Crack Repair, Anchorage Repair, Temporary Protection, Water Testing, Scaffold Access, Rope Access"
  },
  {
    "Problem Tags": "Terra Cotta Failures",
    "Description": "Cracking, displacement, anchorage issues, glaze failure, water intrusion, and deterioration in terra cotta facade elements and systems.",
    "Solution Tags": "Facade Survey, Probe Openings, Terra Cotta Repair, Anchorage Repair, Crack Repair, Selective Demolition, Water Testing, Temporary Protection, Scaffold Access, Rope Access"
  }
];

interface UploadTabProps {
  record: {
    importType: string;
  };
  context: any;
  canEdit: boolean;
  onBeforeImport: (files: File[]) => void;
  onAfterImport: (data: any) => void;
}

export const UploadTab = forwardRef<any, UploadTabProps>(
  ({ record, context, canEdit, onBeforeImport, onAfterImport }, ref) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isImporting, setIsImporting] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const importEntity = async (file: File) => {
      const { importType } = record;

      const selectedBoardSlug = context.selectedBoard.record.slug;

      if (importType === IMPORT_TYPES.marketingTags) {
        const headerError = await validateMarketingTagsCsvHeaders(file);
        if (headerError) {
          showToast({ msg: headerError, type: "error", duration: 5000 });
          return;
        }

        setIsImporting(true);
        try {
          const response = await importMarketingTagsApi(file, selectedBoardSlug);
          const responseData = response.data;
          if (responseData.row_data_with_status?.length > 1) {
            const dataRows = responseData.row_data_with_status.slice(1);
            responseData.rows = responseData.rows.map((row: any, idx: number) => ({
              ...row,
              data: dataRows[idx] || []
            }));
          }
          onAfterImport(responseData);
          return response;
        } catch (error: any) {
          if (error.response) showToast({ msg: serverErrorMsg(), type: "error", duration: 3000 });
          else showToast({ msg: "Failed to import marketing tags", type: "error", duration: 3000 });
          throw error;
        } finally {
          setIsImporting(false);
        }
      }

      const showDialog = shouldShowAsyncDialog("CSV_IMPORT");
      showDialog &&
        context.editorHandler.openEditor(
          EDITOR_KEYS.asyncTaskMessageEditor,
          null,
          { record: "CSV_IMPORT" },
          { useExisting: true, by: "recordType" }
        );

      setIsImporting(true);
      try {
        const response = await importDataApi(file, selectedBoardSlug, importType);
        onAfterImport(response.data);
        return response;
      } catch (error: any) {
        if (error.response) showToast({ msg: serverErrorMsg(), type: "error", duration: 3000 });
        throw error;
      } finally {
        setIsImporting(false);
      }
    };

    // Expose importEntity method to parent via ref
    useImperativeHandle(ref, () => ({
      importEntity
    }));

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) {
        setSelectedFile(null);
        return;
      }

      const file = files[0];

      if (file.size === 0) {
        MessageDialog.alert({
          title: "Empty File Not Allowed",
          message: "Empty file is not allowed. Please upload valid file."
        });
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      setSelectedFile(file);

      if (record.importType === IMPORT_TYPES.taskBudget) {
        onBeforeImport([file]);
      }
    };

    const handleImport = async () => {
      if (!selectedFile) return;
      await importEntity(selectedFile);
    };

    const handleButtonClick = () => {
      fileInputRef.current?.click();
    };

    const isMarketingTags = record.importType === IMPORT_TYPES.marketingTags;

    return (
      <div className="flex flex-col items-center justify-center p-8 gap-6 h-full">
        <div className="text-center space-y-2 max-w-2xl">
          {isMarketingTags ? (
            <>
              <p className="text-sm text-muted-foreground">
                Upload a CSV file with columns: <strong>Problem Tags</strong>, <strong>Description</strong>, <strong>Solution Tags</strong>.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                To create a CSV template for importing your data, click "Export (CSV)" in the Export tab above.
              </p>
              <p className="text-sm text-muted-foreground">Don't change column names or column order.</p>
            </>
          )}
        </div>

        <div className="text-center max-w-2xl">
          {!isMarketingTags && (
            <p className="text-sm text-muted-foreground mb-6">
              When importing new records (versus updating existing records), the ID field in the first column must be
              blank.
            </p>
          )}

          <div className="flex flex-col items-center gap-4">
            <Input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
              className="hidden"
              disabled={!canEdit}
            />
            <Button
              onClick={handleButtonClick}
              variant="default"
              size="lg"
              disabled={!canEdit}
              className="min-w-[250px]"
            >
              Select a file from your device
            </Button>

            {isMarketingTags && (
              <Button
                variant="link"
                className="h-auto p-0 text-sm gap-1"
                onClick={() => {
                  const convertToCSV = (objArray: any) => {
                    const header = Object.keys(objArray[0]).join(",") + "\n";
                    const rows = objArray.map((obj: any) =>
                      Object.values(obj).map((val: any) => `"${val.replace(/"/g, '""')}"`).join(",")
                    ).join("\n");
                    return header + rows;
                  }
                  const csvContent = convertToCSV(DUMMY_MARKETING_TAGS_DATA);
                  const blob = new Blob([csvContent], { type: "text/csv" });
                  downloadFileFromData(blob, "swivu-marketing-tags-template.csv");
                }}
              >
                <Download className="w-3.5 h-3.5" />
                Download template
              </Button>
            )}

            {selectedFile && (
              <div className="flex items-center gap-4 mt-4 p-4 border rounded-lg bg-muted/50 w-full max-w-md">
                <div className="flex-1 min-w-0">
                  <Label className="text-sm font-medium mb-1 block">Ready to import:</Label>
                  <p className="text-sm text-muted-foreground truncate">{selectedFile.name}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {selectedFile && record.importType !== IMPORT_TYPES.taskBudget && (
          <div className="w-full max-w-2xl flex justify-end">
            <Button onClick={handleImport} disabled={isImporting || !canEdit} size="lg" className="min-w-[150px]">
              {isImporting ? "Importing..." : "Import Data"}
            </Button>
          </div>
        )}
      </div>
    );
  }
);

UploadTab.displayName = "UploadTab";
